import getApiClient from '../apiClient';

/**
 * @typedef {import('./types').GetOutletsRequest} GetOutletsRequest
 * @typedef {import('./types').GetOutletsResponse} GetOutletsResponse
 * @typedef {import('./types').OutletItem} OutletItem
 * @typedef {import('./types').CreateOutletRequest} CreateOutletRequest
 * @typedef {import('./types').CreateOutletResponse} CreateOutletResponse
 * @typedef {import('./types').UpdateOutletRequest} UpdateOutletRequest
 */

/**
 * Fetches outlets from the API
 * @param {GetOutletsRequest & { baseUrl?: string }} params - Request parameters including optional baseUrl
 * @returns {Promise<GetOutletsResponse>} - Promise resolving to the response with outlets data
 */
export async function getOutlets({ baseUrl, limit, page, search }) {
  const apiClient = getApiClient(baseUrl);
  const { data } = await apiClient.get('outlets', {
    params: {
      limit,
      page,
      search,
    },
  });
  return data;
}

/**
 * Fetches a single outlet by ID
 * @param {string} outletId - The outlet ID
 * @param {string} [baseUrl] - Optional base URL for the API
 * @returns {Promise<OutletItem>} - Promise resolving to the outlet data
 */
export async function getOutletById(outletId, baseUrl) {
  const apiClient = getApiClient(baseUrl);
  const { data } = await apiClient.get(`outlets/${outletId}`);
  return data;
}

/**
 * Creates a new outlet
 * @param {CreateOutletRequest & { baseUrl?: string }} outletData - Outlet data including optional baseUrl
 * @returns {Promise<CreateOutletResponse>} - Promise resolving to the created outlet response
 */
export async function createOutlet({ baseUrl, ...outletData }) {
  const apiClient = getApiClient(baseUrl);
  const { data } = await apiClient.post('/outlets', outletData);
  return data;
}

/**
 * Updates an existing outlet
 * @param {UpdateOutletRequest & { baseUrl?: string }} outletData - Outlet data including id and optional baseUrl
 * @returns {Promise<void>}
 */
export async function updateOutlet({ baseUrl, id, ...updateData }) {
  const apiClient = getApiClient(baseUrl);
  await apiClient.patch(`/outlets/${id}`, updateData);
}
